import React, { useEffect } from 'react';
import { useSlotPlaceholder } from '@wix/widget-plugins-ooi';
import { useExperiments } from '@wix/yoshi-flow-editor';
import { useStyles } from '@wix/tpa-settings/react';
import loadable from '@wix/yoshi-flow-editor/loadable';
import { resolveId } from '@wix/communities-blog-client-common';
import stylesParams from '@app/components/Post/stylesParams';
import {
  ABOVE_CONTENT_1,
  ABOVE_CONTENT_2,
  PAGE_BOTTOM_1,
  PAGE_BOTTOM_2,
  PAGE_BOTTOM_3,
} from '@app/constants/ooi-slots';
import { OOI_EXPERIMENTS } from '@app/experiments';
import AnimatedLoader from '@app/external/common/components/animated-loader';
import PrintWrapper from '@app/external/common/components/print-wrapper';
import { useActions, useSelector } from '@app/external/common/components/runtime-context';
import { PostTitleTagProvider } from '@app/external/common/context/post-title-tag-context';
import { getRoute } from '@app/external/common/router/router-selectors';
import { getPostByIdOrSlug } from '@app/external/common/selectors/post-selectors';
import { isInPostPreview } from '@app/external/common/services/detect-route';
import scrollToContent from '@app/external/common/services/scroll-to-content';
import { resolvePostSlug } from '@app/external/common/services/slug';
import { getIsPostLoaded } from '@app/external/common/store/is-loaded/is-loaded-selectors';
import { type RoutePostParams } from '../../constants/routes';
import { getReadingSessionId } from '../../selectors/reading-session-id-selector';
import type IPostPageCommentsSection from './post-page-comments-section';
import PostPagePost from './post-page-post';
import PostPageReadingTimeBiElement from './post-page-reading-time-bi-element';
import PostPageReadingTimeBiElementV2 from './post-page-reading-time-bi-element-v2';
import PostPageRelatedPostsSection from './post-page-related-posts-section';
import { useAnalyticsPostViewEffect } from './use-analytics-post-view-effect';
import { useOnChange } from './use-on-change';
import styles from './post-page-root.scss';

const PostPageCommentsSection = loadable(
  () => import(/* webpackChunkName: "post-page-wix-comments" */ './post-page-comments-section'),
) as typeof IPostPageCommentsSection;

type Props = {
  params: RoutePostParams;
};

const PostPageRoot = (props: Props) => {
  const { post, postSlug, readingSessionId, isPostLoaded } = usePostPageSlice(props);
  const [forceComments, setForceComments] = React.useState(false);
  const commentsRef = React.useRef<HTMLDivElement>(null);
  const [AboveContent1SlotsPlaceholder, isAboveContent1SlotEmpty] =
    useSlotPlaceholder(ABOVE_CONTENT_1);
  const [AboveContent2SlotsPlaceholder, isAboveContent2SlotEmpty] =
    useSlotPlaceholder(ABOVE_CONTENT_2);
  const [PageBottom1SlotsPlaceholder, isPageBottom1SlotEmpty] = useSlotPlaceholder(PAGE_BOTTOM_1);
  const [PageBottom2SlotsPlaceholder, isPageBottom2SlotEmpty] = useSlotPlaceholder(PAGE_BOTTOM_2);
  const [PageBottom3SlotsPlaceholder, isPageBottom3SlotEmpty] = useSlotPlaceholder(PAGE_BOTTOM_3);
  const wixStyles = useStyles();
  const { experiments } = useExperiments();
  const showRelatedPosts = wixStyles
    .getStylesForAllBreakpoints()
    .some((style) => style.booleans[stylesParams.showRelatedPosts.key!]);
  const actions = useActions();

  useEffect(() => {
    actions.fetchCurrentUserPromisified();
  }, []);

  useEffect(() => {
    if (!CSS.supports('overflow-anchor: auto')) {
      setForceComments(true);
    }
  }, []);

  useAnalyticsPostViewEffect(post?.title);
  useOnChange(postSlug, () => {
    scrollToContent(styles.root);
  });

  const handleRatingsDisplayClick = () => {
    if (!forceComments) {
      setForceComments(true);
    }

    requestAnimationFrame(() => {
      commentsRef.current?.scrollIntoView({
        block: 'start',
        behavior: 'smooth',
      });
    });
  };

  return (
    <PostTitleTagProvider titleFontParam={stylesParams.textH1Font} fallbackTagName="h1">
      <div className={styles.root} data-hook="post-page">
        {experiments.enabled(OOI_EXPERIMENTS.OPTIMIZE_READ_TIME_BI_EVENT) ? (
          <PostPageReadingTimeBiElementV2 postId={post.id!} />
        ) : (
          <PostPageReadingTimeBiElement post={post} readingSessionId={readingSessionId} />
        )}
        <div className={!isAboveContent1SlotEmpty ? styles.aboveContentSlot : ''}>
          <AboveContent1SlotsPlaceholder />
        </div>
        <div className={!isAboveContent2SlotEmpty ? styles.aboveContentSlot : ''}>
          <AboveContent2SlotsPlaceholder />
        </div>
        <div className={styles.wrapper}>
          <AnimatedLoader isLoading={!isPostLoaded || !post}>
            <PrintWrapper>
              <PostPagePost
                key={resolveId(post)}
                post={post}
                onRatingsDisplayClick={handleRatingsDisplayClick}
              />
            </PrintWrapper>
          </AnimatedLoader>
          {!isPageBottom1SlotEmpty && (
            <div className={styles.pageBottomSlot}>
              <PageBottom1SlotsPlaceholder />
            </div>
          )}
          {showRelatedPosts ? <PostPageRelatedPostsSection post={post} /> : null}
          {!isPageBottom2SlotEmpty && (
            <div className={styles.pageBottomSlot}>
              <PageBottom2SlotsPlaceholder />
            </div>
          )}
          <PostPageCommentsSection
            post={post}
            commentsRef={commentsRef}
            forceComments={forceComments}
          />
          {!isPageBottom3SlotEmpty && (
            <div className={styles.pageBottomSlot}>
              <PageBottom3SlotsPlaceholder />
            </div>
          )}
        </div>
      </div>
    </PostTitleTagProvider>
  );
};

const usePostPageSlice = (ownProps: Props) => {
  const postSlug =
    'postId' in ownProps.params ? ownProps.params.postId : resolvePostSlug(ownProps.params);

  const isPostLoaded = useSelector((state) => getIsPostLoaded(state, postSlug));
  const readingSessionId = useSelector(getReadingSessionId);

  const post = useSelector((state) => {
    const postWithId = getPostByIdOrSlug(state, postSlug)!;

    return isInPostPreview(getRoute(state))
      ? { ...postWithId, ...(postWithId?.draft || {}) }
      : postWithId;
  });

  return {
    post,
    postSlug,
    isPostLoaded,
    readingSessionId,
  };
};

export default PostPageRoot;
